import React, { useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;

function FileManagementPage({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('upload');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [fileType, setFileType] = useState('profile_photo');
  const [employeeId, setEmployeeId] = useState('');
  const [documentType, setDocumentType] = useState('aadhaar');

  const fileTypes = [
    { value: 'profile_photo', label: '📷 Profile Photo', accept: 'image/*' },
    { value: 'document', label: '📄 Document', accept: '.pdf,.jpg,.jpeg,.png' }
  ];

  const documentTypes = [
    'aadhaar',
    'pan',
    'passport',
    'driving_license',
    'educational_certificate',
    'previous_employment',
    'offer_letter',
    'salary_slip',
    'other'
  ];

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size should be less than 10MB');
        return;
      }
      setSelectedFile(file);
      setUploadResult(null);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      alert('Please select a file');
      return;
    }

    if (fileType === 'document' && !employeeId) {
      alert('Please enter employee ID');
      return;
    }

    setLoading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const token = localStorage.getItem('token');
      let endpoint = '';

      if (fileType === 'profile_photo') {
        endpoint = `${API}/api/file-uploads/profile-photo`;
      } else if (fileType === 'document') {
        endpoint = `${API}/api/file-uploads/document?employee_id=${employeeId}&document_type=${documentType}`;
      }

      const response = await axios.post(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      setUploadResult(response.data);
      alert('File uploaded successfully!');
      setSelectedFile(null);
      setUploadProgress(0);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">File Management</h1>
          <p className="mt-2 text-gray-600">Upload profile photos and employee documents</p>
        </div>

        {/* Upload Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-6">Upload Files</h2>

          <form onSubmit={handleUpload} className="space-y-6">
            {/* File Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select File Type *</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {fileTypes.map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFileType(type.value)}
                    className={`px-6 py-4 rounded-lg border-2 transition text-left ${
                      fileType === type.value
                        ? 'border-[#2b3380] bg-[#2b3380] text-white'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="font-semibold">{type.label}</div>
                    <div className="text-sm mt-1 opacity-75">Max size: 10MB</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Employee ID (for documents) */}
            {fileType === 'document' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID *</label>
                <input
                  type="text"
                  required
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                  placeholder="Enter employee ID"
                />
              </div>
            )}

            {/* Document Type (for documents) */}
            {fileType === 'document' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Document Type *</label>
                <select
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                >
                  {documentTypes.map(type => (
                    <option key={type} value={type}>
                      {type.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Choose File *</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#2b3380] transition">
                <input
                  type="file"
                  accept={fileTypes.find(t => t.value === fileType)?.accept}
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer"
                >
                  <div className="text-6xl mb-3">📁</div>
                  <p className="text-lg font-medium text-gray-700 mb-2">
                    Click to select or drag and drop
                  </p>
                  <p className="text-sm text-gray-500">
                    {fileTypes.find(t => t.value === fileType)?.accept}
                  </p>
                </label>
              </div>
              {selectedFile && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    ✅ Selected: <span className="font-semibold">{selectedFile.name}</span> 
                    ({(selectedFile.size / 1024).toFixed(2)} KB)
                  </p>
                </div>
              )}
            </div>

            {/* Upload Progress */}
            {uploadProgress > 0 && uploadProgress < 100 && (
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-[#2b3380] h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !selectedFile}
              className="w-full px-6 py-3 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] transition disabled:bg-gray-400 font-semibold"
            >
              {loading ? 'Uploading...' : 'Upload File'}
            </button>
          </form>

          {/* Upload Result */}
          {uploadResult && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="font-semibold text-green-900 mb-2">✅ Upload Successful</h3>
              <div className="space-y-1 text-sm text-green-800">
                <p>File URL: <span className="font-mono text-xs">{uploadResult.file_url}</span></p>
                {uploadResult.file_id && <p>File ID: {uploadResult.file_id}</p>}
              </div>
            </div>
          )}

          {/* Guidelines */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2">💡 Upload Guidelines</h3>
            <ul className="list-disc list-inside text-sm text-blue-800 space-y-1">
              <li>Profile photos should be clear, professional headshots</li>
              <li>Supported formats: JPG, PNG, PDF (for documents)</li>
              <li>Maximum file size: 10MB per file</li>
              <li>Documents are stored securely and encrypted</li>
              <li>For documents, ensure employee ID is correct</li>
            </ul>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default FileManagementPage;